/**
 * /superadmin/support/{id} — the support team's view of one Contact
 * WorkRight ticket (mockup §6): the thread with internal notes, a reply
 * box that starts as "Reply to Customer" on every ticket (so a note is
 * never sent by accident), status on send, priority, assignee, and the
 * company card (account facts only — no employee data).
 */
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Lock, Send } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import {
  SUPPORT_PRIORITY_LABELS,
  supportCategoryLabel,
  supportStatusLabel,
  type SupportPriority,
  type SupportStatus,
} from "@/lib/support-tickets";
import { SUPPORT_ADMIN_BASE } from "@/lib/api/support-admin";
import {
  useReplySupportAdmin,
  useSupportAdminTicket,
  useSupportAgents,
  useUpdateSupportAdminTicket,
} from "@/hooks/use-support-tickets";
import { PageHeader } from "@/components/shared/page-header";
import { AttachmentPicker } from "@/components/support/attachment-picker";
import { SupportStatusPill } from "@/components/support/status-pill";
import { AttachmentLinks, TicketThread } from "@/components/support/ticket-thread";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export const Route = createFileRoute("/_authgated/superadmin/support/$id")({
  component: SupportAdminTicketPage,
});

const STATUSES: SupportStatus[] = ["open", "in_progress", "waiting_on_customer", "resolved", "closed"];

function SupportAdminTicketPage() {
  const { id } = Route.useParams();
  const ticketId = Number(id) || 0;
  const q = useSupportAdminTicket(ticketId);

  const back = (
    <Button asChild variant="outline" className="gap-1.5">
      <Link to="/superadmin/support">
        <ArrowLeft aria-hidden className="h-4 w-4" />
        Queue
      </Link>
    </Button>
  );

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <>
        <PageHeader title="Ticket" actions={back} />
        <Alert variant="destructive">
          <AlertTitle>Could not open this ticket</AlertTitle>
          <AlertDescription>{q.error ? toApiError(q.error).message : "Ticket not found."}</AlertDescription>
        </Alert>
      </>
    );
  }
  const t = q.data;
  const closed = t.status === "closed";

  return (
    <>
      <PageHeader
        title={t.subject}
        description={`${t.number} · ${t.company?.name ?? ""} · raised ${formatDate(t.created_at)}${t.raised_by_name ? ` by ${t.raised_by_name}` : ""}`}
        actions={back}
      />
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {supportCategoryLabel(t.category)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap break-words text-sm">{t.description}</p>
              <AttachmentLinks ticketId={t.id} files={t.attachments} base={SUPPORT_ADMIN_BASE} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Conversation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <TicketThread ticketId={t.id} messages={t.messages} side="console" attachmentBase={SUPPORT_ADMIN_BASE} />
              {closed ? (
                <p className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
                  This ticket is closed. The customer can raise a new one.
                </p>
              ) : (
                <AdminReplyBox key={t.id} ticketId={t.id} />
              )}
            </CardContent>
          </Card>
        </div>
        <aside className="space-y-4">
          <TicketControls ticketId={t.id} status={t.status} priority={t.priority} assigneeId={t.assignee_id} closed={closed} />
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Company</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {t.company ? (
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <dt className="text-muted-foreground">Name</dt>
                  <dd>
                    <Link
                      to="/superadmin/tenants/$id"
                      params={{ id: String(t.company.id) }}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {t.company.name}
                    </Link>
                  </dd>
                  <dt className="text-muted-foreground">Slug</dt>
                  <dd className="break-all font-mono text-xs">{t.company.slug}</dd>
                  <dt className="text-muted-foreground">Plan</dt>
                  <dd>{t.company.plan_name ?? "No plan"}</dd>
                  <dt className="text-muted-foreground">People</dt>
                  <dd className="tabular-nums">{t.company.active_headcount}</dd>
                </dl>
              ) : (
                <p className="text-muted-foreground">Company details unavailable.</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Sent With the Ticket</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="text-muted-foreground">From</dt>
                <dd className="break-all">
                  {t.raised_by_name}
                  {t.raised_by_email && <span className="block text-xs text-muted-foreground">{t.raised_by_email}</span>}
                </dd>
                {Object.entries(t.context ?? {}).map(([k, v]) => (
                  <div key={k} className="contents">
                    <dt className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</dt>
                    <dd className="break-all text-xs">{v}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        </aside>
      </div>
    </>
  );
}

function TicketControls({
  ticketId,
  status,
  priority,
  assigneeId,
  closed,
}: Readonly<{ ticketId: number; status: SupportStatus; priority: SupportPriority; assigneeId: number | null; closed: boolean }>) {
  const { user } = useAuth();
  const update = useUpdateSupportAdminTicket(ticketId);
  const agentsQ = useSupportAgents();

  async function save(req: Parameters<typeof update.mutateAsync>[0], done: string) {
    try {
      await update.mutateAsync(req);
      toast.success(done);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Ticket</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <SupportStatusPill status={status} side="console" />
        <div className="space-y-1">
          <Label htmlFor="sa-status">Status</Label>
          <Select
            value={status}
            disabled={closed || update.isPending}
            onValueChange={(v) => void save({ status: v as SupportStatus }, "Status updated.")}
          >
            <SelectTrigger id="sa-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {supportStatusLabel(s, "console")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sa-priority">Priority</Label>
          <Select
            value={priority}
            disabled={closed || update.isPending}
            onValueChange={(v) => void save({ priority: v as SupportPriority }, "Priority updated.")}
          >
            <SelectTrigger id="sa-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(SUPPORT_PRIORITY_LABELS) as SupportPriority[]).map((p) => (
                <SelectItem key={p} value={p}>
                  {SUPPORT_PRIORITY_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="sa-assignee">Assignee</Label>
          <Select
            value={assigneeId ? String(assigneeId) : "none"}
            disabled={closed || update.isPending}
            onValueChange={(v) =>
              void save({ assignee_id: v === "none" ? null : Number(v) }, v === "none" ? "Unassigned." : "Assigned.")
            }
          >
            <SelectTrigger id="sa-assignee">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {(agentsQ.data ?? []).map((a) => (
                <SelectItem key={a.id} value={String(a.id)}>
                  {a.name}
                  {a.id === user?.user_id ? " (Me)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {user?.user_id && assigneeId !== user.user_id && !closed && (
            <Button
              variant="outline"
              size="sm"
              className="mt-1 w-full pointer-coarse:min-h-11"
              disabled={update.isPending}
              onClick={() => void save({ assignee_id: user.user_id }, "Assigned to you.")}
            >
              Assign to Me
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AdminReplyBox({ ticketId }: Readonly<{ ticketId: number }>) {
  const reply = useReplySupportAdmin(ticketId);
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [body, setBody] = useState("");
  const [status, setStatus] = useState<SupportStatus>("waiting_on_customer");
  const [files, setFiles] = useState<File[]>([]);
  const note = mode === "note";

  async function send() {
    const text = body.trim();
    if (!text) return;
    try {
      await reply.mutateAsync({ body: text, internal: note, status: note ? undefined : status, files });
      setBody("");
      setFiles([]);
      setMode("reply");
      toast.success(note ? "Internal note added." : "Reply sent to the customer.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <div
      className={cn(
        "space-y-3 rounded-lg border p-3",
        note && "border-dashed border-amber-400 bg-amber-50 dark:border-amber-500 dark:bg-amber-950/30",
      )}
    >
      <ToggleGroup
        type="single"
        value={mode}
        onValueChange={(v) => v && setMode(v as "reply" | "note")}
        aria-label="Message type"
        className="justify-start"
      >
        <ToggleGroupItem value="reply" className="pointer-coarse:min-h-11">
          Reply to Customer
        </ToggleGroupItem>
        <ToggleGroupItem value="note" className="gap-1.5 pointer-coarse:min-h-11">
          <Lock aria-hidden className="h-3.5 w-3.5" />
          Internal Note
        </ToggleGroupItem>
      </ToggleGroup>
      <Label htmlFor="sa-reply" className="sr-only">
        {note ? "Internal note" : "Reply to customer"}
      </Label>
      <Textarea
        id="sa-reply"
        rows={4}
        maxLength={10000}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={note ? "Only the WorkRight team sees this" : "The customer gets this in the app and by email"}
        className="bg-background"
      />
      <AttachmentPicker files={files} onChange={setFiles} label="Attach" />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        {!note ? (
          <div className="space-y-1 sm:w-56">
            <Label htmlFor="sa-send-status">Then Set Status To</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as SupportStatus)}>
              <SelectTrigger id="sa-send-status" className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.filter((s) => s !== "open").map((s) => (
                  <SelectItem key={s} value={s}>
                    {supportStatusLabel(s, "console")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Customer can&rsquo;t see this note.</p>
        )}
        <Button onClick={() => void send()} disabled={reply.isPending || !body.trim()} className="gap-1.5">
          {reply.isPending ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : <Send aria-hidden className="h-4 w-4" />}
          {note ? "Add Note" : "Send Reply"}
        </Button>
      </div>
    </div>
  );
}
